/**
 * How far along something is: an export, an upload, today's word goal (cockatrice-ui `src/ui/ProgressBar.jsx`, ported
 * to hyperscript; PLAN.md §0).
 * Leave out `value` for indeterminate ("Preparing…"). The value text is always written out, never colour alone.
 */
import './css/progress-bar.css';
import { createUniqueId } from 'solid-js';
import { el, when } from './solid.ts';
import type { Child } from './solid.ts';
import { uiText } from './text.ts';

export interface ProgressBarProps {
  /** Current value. Leave out for indeterminate. */
  readonly value?: number | undefined;
  /** Default 100. */
  readonly max?: number | undefined;
  /** Visible label; names the progressbar. */
  readonly label?: string | undefined;
  /** Accessible name when there is no label, or one that says more than the label shown (it wins). Default "Progress". */
  readonly ariaLabel?: string | undefined;
  /** Written value, e.g. "640 of 1,000 words". Default "NN%". */
  readonly valueText?: string | undefined;
  /** The value as heard, when it says more than the one shown (shown "Goal 80,000", heard "48,210 of 80,000 words"). */
  readonly ariaValueText?: string | undefined;
  /** Show the value text. Default true. */
  readonly showValue?: boolean | undefined;
  /** Unknown progress. */
  readonly indeterminate?: boolean | undefined;
  /** Default "accent". Always with words. */
  readonly tone?: 'accent' | 'success' | 'warn' | 'danger' | undefined;
  /** 4 or 6 px track. Default "md". */
  readonly size?: 'sm' | 'md' | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
}

export default function ProgressBar(props: ProgressBarProps): Child {
  const id = `ui-pb-${createUniqueId()}`;
  const max = (): number => props.max ?? 100;
  const value = (): number | undefined => (typeof props.value === 'number' && props.indeterminate !== true ? props.value : undefined);
  const known = (): boolean => value() !== undefined;
  const pct = (): number => Math.max(0, Math.min(100, ((value() ?? 0) / max()) * 100));
  const text = (): string | undefined => (known() ? (props.valueText ?? `${Math.round(pct())}%`) : props.valueText);
  const labelled = (): boolean => props.label !== undefined && props.label !== '';
  const showText = (): boolean => props.showValue !== false && Boolean(text());

  return el(
    'div',
    {
      class: () =>
        `ui-progress-bar ui-progress-bar--${props.size ?? 'md'} ui-progress-bar--${props.tone ?? 'accent'}${props.class !== undefined && props.class !== '' ? ` ${props.class}` : ''}`,
    },
    when(
      () => labelled() || showText(),
      () =>
        el(
          'div',
          { class: 'ui-progress-bar__meta' },
          el('span', { id, class: 'ui-progress-bar__label' }, () => props.label),
          when(showText, () => el('span', { class: 'ui-progress-bar__value', 'aria-hidden': 'true' }, text)),
        ),
    ),
    el(
      'div',
      {
        class: () => (known() ? 'ui-progress-bar__track' : 'ui-progress-bar__track is-indeterminate'),
        role: 'progressbar',
        // PLAN.md §3, step 2: a given ariaLabel names the bar even beside a visible label.
        'aria-labelledby': () => (labelled() && props.ariaLabel === undefined ? id : undefined),
        'aria-label': () => (labelled() && props.ariaLabel === undefined ? undefined : (props.ariaLabel ?? uiText()('ui.progress-bar.label'))),
        'aria-valuemin': () => (known() ? 0 : undefined),
        'aria-valuemax': () => (known() ? max() : undefined),
        'aria-valuenow': value,
        'aria-valuetext': () => props.ariaValueText ?? text(),
      },
      el('div', { class: 'ui-progress-bar__fill', style: () => (known() ? { width: `${pct()}%` } : undefined) }),
    ),
  );
}
